import "./lib/addressRequestToken";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initializePushNotifications, addPushNotificationListeners } from "./utils/pushNotifications";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { restoreNativeAuthSession } from "./utils/nativeAuthStorage";

// Auto-recover from stale chunk hashes after a new deploy.
// When the browser tries to load an old code-split chunk that no longer
// exists, force a one-time hard reload so it fetches the fresh index.html.
if (typeof window !== "undefined") {
  const isChunkLoadError = (msg?: string) =>
    !!msg && /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg);

  const tryReload = () => {
    const key = "__chunk_reload_at";
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
  };

  // Vite fires this when a lazy page chunk from an older deploy is gone.
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    tryReload();
  });

  window.addEventListener("error", (e) => {
    if (isChunkLoadError(e?.message)) tryReload();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e?.reason && (e.reason.message || String(e.reason))) || "";
    if (isChunkLoadError(msg)) tryReload();
  });
}

const bootstrap = async () => {
  // On native iOS/Android, restore Supabase auth tokens from native
  // Preferences into localStorage BEFORE React mounts and the Supabase
  // client reads its session. This prevents the WebView from logging the
  // user out when the app is backgrounded.
  await restoreNativeAuthSession();

  if (typeof window !== "undefined") {
    // Install registration listeners before requesting a token: native callbacks
    // can arrive immediately when permission has already been granted.
    void addPushNotificationListeners()
      .then(() => initializePushNotifications())
      .catch(console.error);
  }

  createRoot(document.getElementById("root")!).render(<App />);

  // Hide the native splash screen once React has mounted and painted the first frame.
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {
          // Plugin may not be available; safe to ignore
        });
      });
    });
  }
};

bootstrap();
